import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { UserNotificationService } from '../../../core/services/user-notification.service';
import { UserNotificationModel } from '../../../core/services/user-notification-api.service';

/** Matches legacy `UserNotificationBannerComponent.isNotificationActive()`. */
function isNotificationActive(userNotification: UserNotificationModel | null): boolean {
  if (!userNotification) {
    return false;
  }

  const now = Date.now();
  const start = userNotification.Start ? new Date(userNotification.Start).getTime() : null;
  const end = userNotification.End ? new Date(userNotification.End).getTime() : null;
  const isCurrentlyActive = (!start || start <= now) && (!end || end >= now);

  return userNotification.IsEnabled && isCurrentlyActive && !!userNotification.Content;
}

/**
 * Ports legacy `UserNotificationBannerComponent` — an app-wide banner shown at the
 * top of every page while an admin-configured notification is active (enabled, within
 * its optional start/end window, and has content). Mounted once in `DefaultLayoutComponent`.
 */
@Component({
  selector: 'app-user-notification-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-notification-banner.component.html',
  styleUrl: './user-notification-banner.component.scss',
})
export class UserNotificationBannerComponent implements OnInit, OnDestroy {
  private readonly userNotificationService = inject(UserNotificationService);
  private readonly cdr = inject(ChangeDetectorRef);

  userNotification: UserNotificationModel | null = null;
  isActive = false;

  private subscription: Subscription | null = null;

  ngOnInit(): void {
    this.subscription = this.userNotificationService.userNotificationSubject.subscribe((userNotification) => {
      this.userNotification = userNotification;
      this.isActive = isNotificationActive(userNotification);
      this.cdr.markForCheck();
    });

    // Non-blocking: suppress errors, banner is not a critical feature.
    this.userNotificationService.refresh().catch(() => { /* ignore */ });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }
}
